import React, { useState, useEffect } from 'react';
import AppSidebar from '../../components/AppSidebar';
import AppHeader from '../../components/AppHeader';
import PageHeader from '../../components/PageHeader';
import ProfilingService from '../../services/ProfilingService';
import UsersService from '../../services/UsersService';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import ProductService from '../../services/ProductService';
import { industryOptions, empoyeeCountOptions, profilingStatusOptions } from '../../utils/OptionUtils';
import { Country, State, City }  from 'country-state-city';
import SelectTag from '../../components/SelectTag';

const CreateProfiling = () => {
  const navigate = useNavigate();
  const { register, handleSubmit, control, formState: { errors }, setValue } = useForm({
    defaultValues: {
      source: 'Profliling',
    }

  });
  const [assigneeList, setAssigneeList] = useState();
  const [productList, setProductList] = useState();
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [countryList, setCountryList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);

  useEffect(() => {
    const fetchAssignees = async () => {
      try {
        const response = await UsersService.list();
        const assignees = response.data.data;
        const formattedAssigneeList = assignees.map((assignee) => ({
          value: assignee.id,
          label: assignee.name + '-' + assignee.email,
        }));
        setAssigneeList(formattedAssigneeList);
      } catch (err) {
        toast.error('Failed to load assignees');
      }
    };

    const fetchProducts = async () => {
      try {
        const response = await ProductService.list();
        const products = response.data.data;
        const formattedProductList = products.map((product) => ({
          value: product.id,
          label: product.name,
        }));
        setProductList(formattedProductList);
      } catch (err) {
        toast.error('Failed to load products');
      }
    };

    fetchAssignees();
    fetchProducts();
    fetchCountries();
  }, [ ]);

  const onSubmit = async (data) => {

    const requestBody = {
      data: {
        ...data,
        assigned_to: data?.assigned_to?.value || null,
        product_id: data?.product_id?.value || null,
      }
    };

    try {
      const response = await ProfilingService.createProfiling(requestBody);
      if (response.status === 201) {
        toast.success('Profiling data created successfully');
        setTimeout(() => {
          navigate('/profiling');
        }, 2000);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to create profiling data');
      console.error('Error creating profiling data:', error);
    }
  };

  // Function to only allow numbers
  const handleInputChange = (e, maxLength) => {
    e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, maxLength);
  };
  // Function to fetch country list
  const fetchCountries = () => {
    const countries = Country.getAllCountries().map((country) => ({
      value: country.isoCode,
      label: country.name,
    }));
    setCountryList(countries);
  };
  const handleCountryChange = (selectedCountry) => {
    if (selectedCountry) {
      setSelectedCountry(selectedCountry);
      const states = State.getStatesOfCountry(selectedCountry.value).map(state => ({
        value: state.isoCode,
        label: state.name
      }));
      setStateList(states);
      setValue("state", null);
    } else {
      setStateList([]);
      setValue("state", null);
    }
  };
  
  const handleStateChange = (selectedState) => {
    if (selectedState && selectedCountry) {
      const cities = City.getCitiesOfState(selectedCountry.value, selectedState.value).map(city => ({
        value: city.name,
        label: city.name
      }));
      setCityList(cities);
      setValue("city", null);
    } else {
      setCityList([]);
      setValue("city", null);
    }
  };  

  return (
    <>
      <AppSidebar />
      <div className="main-content">
        <AppHeader />
        <div className="container-fluid">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-10 col-xl-12">
              <PageHeader title='Create Profiling' button_label1="Back"
                link_to1="/profiling" className1="btn btn-primary btn-sm" />
              <div className="card p-4">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="row justify-content-between align-items-center">
                    <div className="row mb-4">
                      <div className="col-md-4">
                        <label className="form-label">Status</label>
                        <select {...register('status')}
                          className="form-select" defaultValue="pending">
                          <option value="">Select Status</option>
                            {profilingStatusOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                        </select>
                        {errors?.status && <p className="text-danger">{errors?.status?.message}</p>}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Source</label>
                        <input
                          type="text"
                          {...register('source', {
                            required: 'The source field is required.',
                            minLength: {
                              value: 2,
                              message: "Source name must be longer than or equal to 2 characters"
                            }
                          })}
                          className={`form-control ${errors?.source ? 'is-invalid' : ''}`}
                          readOnly
                        />
                        {errors?.source && <div className="invalid-feedback">{errors?.source?.message}</div>}
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label className="form-label">Assigned To</label>
                          <Controller
                            name="assigned_to"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <Select
                                {...field}
                                options={assigneeList}
                                closeMenuOnSelect={true}
                                isClearable={true}
                              />
                            )}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group pb-4">
                        <label className="form-label">Product</label>
                        <Controller
                          name="product_id"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              options={productList}
                              closeMenuOnSelect={true}
                              isClearable={true}
                            />
                          )}
                        />
                        {errors?.product_id && <div className="invalid-feedback">{errors?.product_id?.message}</div>}
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group pb-4">
                        <label className="form-label">Company</label>
                        <input
                          type="text"
                          {...register('company')}
                          className="form-control"
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group pb-4">
                        <label className="mb-1 form-label">Email address</label>
                        <input
                          type="email"
                          {...register('email', {
                            required: "The email field is required.",
                            pattern: {
                              value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                              message: "Email must be a valid email address"
                            }
                          })}
                          className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                        />
                        {errors?.email && <div className="invalid-feedback">{errors?.email?.message}</div>}
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group pb-4">
                        <label className="mb-1 form-label">Phone no</label>
                        <input
                          type="tel"
                          maxLength={10}
                          {...register('phone', {
                            minLength: {
                              value: 10,
                              message: "The phone number must be 10 digits number"
                            },
                            maxLength: {
                              value: 10,
                              message: "The phone number must be 10 digits number"
                            },
                            pattern: {
                              value: /^[0-9]+$/,
                              message: "Phone number must contain only digits"
                            }
                          })}
                          onInput={(e) => handleInputChange(e, 10)}
                          className={`form-control ${errors?.phone ? 'is-invalid' : ''}`}
                        />
                        {errors?.phone && <div className="invalid-feedback">{errors?.phone?.message}</div>}
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group pb-4">
                        <label className="mb-1 form-label">Mobile No</label>
                        <input
                          type="tel"
                          maxLength={10}
                          {...register('mobile_no', {
                            required: "The mobile number field is required.",
                            minLength: {
                              value: 10,
                              message: "The mobile number must be 10 digits number"
                            },
                            maxLength: {
                              value: 10,
                              message: "The mobile number must be 10 digits number"
                            },
                            pattern: {
                              value: /^[0-9]+$/,
                              message: "Mobile number must contain only digits"
                            }
                          })}
                          onInput={(e) => handleInputChange(e, 10)}
                          className={`form-control ${errors?.mobile_no ? 'is-invalid' : ''}`}
                        />
                        {errors?.mobile_no && <div className="invalid-feedback">{errors?.mobile_no?.message}</div>}
                      </div>
                    </div>
                    <div className="col-12 col-md-6 mb-3">
                      <div className="form-group">
                        <label className="form-label">Industry Type</label>
                        <Controller
                          name="industry_type"
                          control={control}
                          rules={{ required: "The industry field is required" }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              options={industryOptions}
                              placeholder="Select an industry"
                              isSearchable
                              classNamePrefix="react-select"
                              value={industryOptions.find(option => option.value === field.value) || null}
                              onChange={(selectedOption) => {
                                field.onChange(selectedOption?.value);
                                setShowOtherInput(selectedOption?.value === 'other');
                              }}
                            />
                          )}
                        />
                        {errors.industry_type && <span className="text-danger">The industry field is required</span>}
                      </div>
                      {showOtherInput && (
                        <div className="form-group mt-2">
                          <input
                            type="text"
                            {...register('specify_other')}
                            className="form-control"
                            placeholder="Please specify the industry"
                          />
                        </div>
                      )}
                    </div>
                    <div className="col-12 col-md-6 mb-3">
                      <div className="form-group">
                        <label className="form-label">No. of employees</label>
                        <SelectTag
                          name="employee_count"
                          control={control}
                          label="employee count"
                          isRequired={true}
                          options={empoyeeCountOptions}
                        />
                        {errors.employee_count && <span className="text-danger">{errors?.employee_count?.message}</span>}
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group pb-4">
                        <label className="form-label">Address</label>
                        <textarea
                          {...register('address')}
                          className="form-control"
                          rows={1}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group pb-4">
                        <label className="form-label">Country</label>
                        <SelectTag
                          name="country"
                          control={control}
                          label="country"
                          options={countryList}
                          onChange={(selectedCountry) => {
                            handleCountryChange(selectedCountry);
                            setValue("state", null);
                            setValue("city", null);
                          }}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group pb-4">
                        <label className="form-label">State</label>
                          <SelectTag
                            name="state"
                            control={control}
                            label="state"
                            options={stateList}
                            onChange={(selectedState) => {
                              handleStateChange(selectedState); // Fetch cities based on selected state
                              setValue("city", null);
                            }}
                          />

                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group pb-4">
                        <label className="form-label">City</label>
                        <SelectTag
                          name="city"
                          control={control}
                          label="city"
                          options={cityList}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group pb-4">
                        <label className="form-label">Name</label>
                        <input
                          type="text"
                          {...register('name', {
                            required: "The name field is required.",
                            minLength: {
                              value: 2,
                              message: "Name must be longer than or equal to 2 characters"
                            }
                          })}
                          className={`form-control ${errors?.name ? 'is-invalid' : ''}`}
                        />
                        {errors?.name && <div className="invalid-feedback">{errors?.name?.message}</div>}
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group pb-4">
                        <label className="form-label">Department</label>
                        <input
                          type="text"
                          {...register('department')}
                          className="form-control"
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group pb-4">
                        <label className="form-label">Designation</label>
                        <input
                          type="text"
                          {...register('designation')}
                          className="form-control"
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group pb-4">
                        <label className="form-label">Zip Code</label>
                        <input
                          maxLength={6}
                          type="tel"
                          {...register('zipcode', {
                            required: "The zipcode field is required.",
                            minLength: {
                              value: 6,
                              message: "Zipcode must be longer than or equal to 6 numbers"
                            },
                            pattern: {
                              value: /^[0-9]+$/,
                              message: ""
                            }
                          })}
                          onInput={(e) => handleInputChange(e, 6)}
                          className={`form-control ${errors?.zipcode ? 'is-invalid' : ''} `}
                        />
                        {errors?.zipcode && <div className="invalid-feedback">{errors?.zipcode?.message}</div>}
                      </div>
                    </div>
                  </div>
                  <div className="text-end">
                    <button type="submit" className="btn btn-primary">
                      Create
                    </button>
                  </div>
                </form>
                <ToastContainer />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateProfiling;
